import { Fragment, useState } from "react";
import { useEffect } from "react";
import { PuffLoader } from "react-spinners";

import { Delete, Feedback } from "@mui/icons-material";
import { Box, Divider, Fab, Grid, lighten, Stack, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

import constants from "../../assets/constants";
import AlertDialog from "../dialogs/alertDialog";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import BotpressUserFeedbackDialog from "./botpressUserFeedbackDialog.js";

const ChatBubble = props => {
  const { msg, theme, setDisplayTimeStamp, highlightId = null } = props;
  let userMessage = false;
  let messageText = msg.messageText;
  let timeStamp = new Date(msg.date).toLocaleString();
  let borderRadius = "16px 16px 16px 4px";

  if (msg.direction === "incoming") {
    userMessage = true;
    timeStamp = `${timeStamp} : USER`;
    borderRadius = "16px 16px 4px 16px";
  } else {
    timeStamp = `BOT : ${timeStamp}`;
  }
  const [isHovered, setIsHovered] = useState(false);

  // Event handler for when the mouse enters the trigger element
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  // Event handler for when the mouse leaves the trigger element
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = (e, message) => {
    e.stopPropagation();
    props.handleMessageClick(message);
  };

  return (
    <Grid
      container
      flexDirection={"column"}
      flexWrap={"wrap"}
      id={"grid base"}
      onClick={() => setDisplayTimeStamp(display => !display)}
    >
      <Grid
        sx={[
          { marginTop: 1, marginBottom: 0 },
          userMessage && {
            alignSelf: "flex-end",
            justifyContent: "flex-end",
            textAlign: "right",
          },
        ]}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ marginLeft: theme.spacing(1.5), marginRight: theme.spacing(1.5) }}
        >
          {timeStamp}
          {/* {`${msg.direction === "incoming" ? "USER - " : ""}${new Date(msg.date).toLocaleString()}${msg.direction === "incoming" ? "" : " - BOT"}`} */}
        </Typography>
      </Grid>
      <Grid
        container
        spacing={0}
        flexDirection={"column"}
        size={{ xs: 9 }}
        width={"auto"}
        marginTop={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={e => handleClick(e, msg)}
        sx={[
          {
            position: "relative",
            cursor: "pointer",
            userSelect: "none",
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            borderRadius: borderRadius,
            maxWidth: "90%",
            padding: "4px",
            paddingRight: "8px",
            paddingLeft: "8px",
          },
          userMessage && {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            alignSelf: "flex-end",
            justifyContent: "flex-end",
            textAlign: "right",
          },
        ]}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.secondary.contrastText,
            padding: "4px",
            paddingRight: "8px",
            paddingLeft: "8px",
            backgroundClip: "padding-box",
            whiteSpace: "pre-wrap",
          }}
        >
          {messageText}
        </Typography>
        {!msg.hasFeedback && (
          <Fab
            variant="extended"
            color="primary"
            sx={[
              {
                position: "absolute",
                display: "none",
                right: -24,
                top: -24,
                boxShadow: theme.shadows[3],
              },
              !userMessage &&
                isHovered &&
                highlightId == null && {
                  display: "flex",
                },
            ]}
          >
            <Feedback size="small" sx={{ mr: 1 }} />
            Click to provide feedback
          </Fab>
        )}
        {msg.hasFeedback && (
          <Fab
            variant="extended"
            color="primary"
            sx={[
              {
                position: "absolute",
                display: "flex",
                right: -24,
                top: -24,
                boxShadow: theme.shadows[3],
              },
            ]}
          >
            <Feedback size="small" sx={{ mr: 1 }} />
            {constants.feedbackStatus.find(item => item.value === parseInt(msg.status)).label +
              " Feedback"}
          </Fab>
        )}
        {msg.hasFeedback && (
          <Typography
            variant="body2"
            sx={[
              {
                position: "absolute",
                display: "none",
                left: 8,
                top: 8,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: "16px",
                padding: "8px",
                maxWidth: "75%",
                whiteSpace: "pre-line",
                boxShadow: theme.shadows[3],
              },
              !userMessage &&
                isHovered &&
                highlightId == null && {
                  display: "flex",
                },
            ]}
          >
            {"Feedback: \r\n" + msg.feedback}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const BotpressConversationDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    onDelete,
    conversation,
    conversationFeedback,
    updateFeedbackRequest,
    deleteFeedbackRequest,
    userODM,
    dialogTitle = "Transcription",
    okMessage = "Close",
    maxWidth = "md",
    fullWidth = true,
    conversationId = null,
    messageId = null,
  } = props;

  const theme = useTheme();

  const onAcceptHandler = () => {
    onAccept();
  };

  const onCloseHandler = () => {
    onClose();
  };

  const [showConfirm, setShowConfirm] = useState(false);

  const [messages, setMessages] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [displayTimeStamp, setDisplayTimeStamp] = useState(true);

  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleMessageClick = message => {
    //
    let existingFeedback = feedback.find(fb => fb.messageId === message.messageId);
    if (existingFeedback == null) {
      //create new feedback
      existingFeedback = {
        id: null,
        messageId: message.messageId,
        value: "negative",
        comment: "",
        message: message.messageText,
        archived: false,
        status: 1,
        userName: userODM
          ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
          : "Unknown User",
        conversationId: conversationId,
      };
    }
    setSelectedFeedback(existingFeedback);
  };

  const formatMessage = (message, conversationFeedback) => {
    let messageText = "";

    if (message.type === "text" || message.type === "quick_reply") {
      messageText += message.payload.text;
    } else if (message.type === "choice") {
      messageText += `${message.payload.text}: ${message.payload.options.map(option => option.label).join(" / ")}`;
    } else {
      messageText += `Media message of type ${message.type}`;
    }

    const feedback = conversationFeedback.find(fb => fb.messageId === message.id);

    return {
      messageText: messageText,
      direction: message.direction,
      date: message.createdAt,
      messageId: message.id,
      status: feedback != null ? feedback.status : 0,
      hasFeedback: feedback != null,
      feedback: feedback != null ? feedback.comment : "",
    };
  };

  const onDeleteHandler = () => {
    onDelete(conversationId);
  };

  useEffect(() => {
    if (conversation != null && conversationFeedback != null) {
      let allMessages = [];
      allMessages = allMessages.concat(
        conversation.map(message => formatMessage(message, conversationFeedback)),
      );
      setMessages(allMessages.sort((a, b) => new Date(a.date) - new Date(b.date)));
      setFeedback(conversationFeedback);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, conversationFeedback]);

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        {messages == null && (
          <Grid
            sx={{ textAlign: "center", marginTop: "20px", minimumHeight: "400px" }}
            container
            direction="column"
            alignItems="center"
            spacing={2}
          >
            <Grid>
              <PuffLoader size={100} />
            </Grid>
            <Grid>Loading...</Grid>
          </Grid>
        )}
        {messages != null && (
          <Grid>
            <Grid
              container
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              spacing={2}
              margin={"0px"}
              padding={"0px"}
            >
              <Grid size={2}>
                <Divider direction={"horizontal"} />
              </Grid>
              <Grid size={"auto"}>
                <Typography
                  variant="overline"
                  color={lighten(theme.palette.commonground.contrastText, 0.5)}
                >
                  Conversation Start
                </Typography>
              </Grid>
              <Grid size={2}>
                <Divider direction={"horizontal"} />
              </Grid>
            </Grid>
            <Grid>
              {messages.map((msg, index) => (
                <Stack
                  direction="column"
                  justifyContent="flex-end"
                  key={index}
                  spacing={2}
                  size={{ xs: 12 }}
                >
                  <ChatBubble
                    key={`chatBubble${index}`}
                    msg={msg}
                    theme={theme}
                    highlightId={messageId}
                    displayTimeStamp={displayTimeStamp}
                    setDisplayTimeStamp={setDisplayTimeStamp}
                    handleMessageClick={handleMessageClick}
                  />
                </Stack>
              ))}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          <OmniResponsiveButton
            icon={<Delete />}
            onClick={() => setShowConfirm(true)}
            buttonText={"Delete"}
            tooltipTitle=""
          />
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this conversation?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
      {selectedFeedback != null && (
        <BotpressUserFeedbackDialog
          open={selectedFeedback != null}
          userFeedbackRecord={selectedFeedback}
          onAccept={theRecord => {
            updateFeedbackRequest({ feedbackRecord: theRecord });
            setSelectedFeedback(null);
            // onCloseHandler();
          }}
          onDelete={theRecord => {
            deleteFeedbackRequest({ feedbackId: theRecord.id });
            setSelectedFeedback(null);
            // onCloseHandler();
          }}
          onClose={() => {
            setSelectedFeedback(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressConversationDialog;
